import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { brl, fmtDateTime, num } from "@/lib/format";
import { downloadReport, diffClass, signed, ROW_STYLE, INVENTORY_STATUS_STYLE } from "@/lib/smartInventory";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, ScanLine, Search, Check, RotateCcw, Download, ChevronDown, ChevronUp, Users, MapPin } from "lucide-react";

const ACTIONS = [
  ["abrir", "Abrir para contagem", "bg-sky-700"],
  ["iniciar", "Iniciar contagem", "bg-sky-700"],
  ["encerrar_contagem", "Encerrar contagem", "bg-amber-700"],
  ["enviar_aprovacao", "Enviar para aprovação", "bg-amber-700"],
  ["voltar_conferencia", "Voltar para conferência", "bg-slate-700"],
  ["reabrir_contagem", "Reabrir contagem", "bg-slate-700"],
  ["finalizar", "Finalizar inventário", "bg-emerald-700"],
  ["cancelar", "Cancelar inventário", "bg-rose-800"],
];
const FILTERS = [
  ["", "Todos"], ["divergencia", "Com divergência"], ["sem_divergencia", "Sem divergência"],
  ["nao_encontrado", "Não encontrados"], ["nao_contado", "Ainda não contados"],
  ["duplicidade", "Possível duplicidade"], ["recontagem", "Aguardando recontagem"],
  ["pendente_aprovacao", "Prontos para aprovar"], ["aprovado", "Aprovados"],
];
const FILTER_STATUSES = {
  divergencia: ["DIVERGENTE", "NAO_ENCONTRADO"], sem_divergencia: ["CONFERE"], nao_encontrado: ["NAO_ENCONTRADO"],
  nao_contado: ["NAO_CONTADO"], recontagem: ["RECONTAGEM", "ESTOQUE_MOVIMENTADO", "CONTAGEM_DUPLICADA"],
  pendente_aprovacao: ["CONFERE", "DIVERGENTE", "NAO_ENCONTRADO"], aprovado: ["APROVADO"],
};

function ReasonDialog({ state, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  const [qty, setQty] = useState("");
  useEffect(() => { if (state) { setReason(""); setQty(state.qty != null ? String(state.qty) : ""); } }, [state]);
  if (!state) return null;
  const needsReason = state.required !== false;
  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader><DialogTitle>{state.title}</DialogTitle></DialogHeader>
        {state.text && <p className="text-sm text-slate-300">{state.text}</p>}
        {state.qty != null && <div><Label htmlFor="fix-qty">Quantidade física correta</Label>
          <Input id="fix-qty" type="number" inputMode="numeric" min="0" step="1" value={qty} onChange={(e) => setQty(e.target.value)} className="bg-slate-950 border-slate-700 mt-1" /></div>}
        <div><Label htmlFor="fix-reason">{needsReason ? "Justificativa (obrigatória)" : "Observação (opcional)"}</Label>
          <Input id="fix-reason" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className="bg-slate-950 border-slate-700 mt-1" /></div>
        <DialogFooter>
          <Button variant="ghost" disabled={busy} onClick={onClose}>Voltar</Button>
          <Button disabled={busy || (needsReason && !reason.trim()) || (state.qty != null && qty === "")}
            onClick={() => onConfirm(reason.trim(), qty === "" ? null : Number(qty))} className={state.danger ? "bg-rose-700 text-white" : "bg-sky-700 text-white"}>
            {busy ? "Salvando..." : state.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConferenceRow({ r, inv, onAction, open, onToggle }) {
  const perms = inv.permissoes || {};
  const editable = !["FINALIZADO", "CANCELADO"].includes(inv.status) && r.status !== "APROVADO";
  const approvable = ["EM_CONTAGEM", "AGUARDANDO_CONFERENCIA", "AGUARDANDO_APROVACAO"].includes(inv.status)
    && ["CONFERE", "DIVERGENTE", "NAO_ENCONTRADO"].includes(r.status) && perms["inventario.aprovar"] && perms["estoque.ajustar"];
  return (
    <div className="border-t border-slate-800 px-3 py-3">
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 md:grid-cols-[minmax(0,2.2fr)_repeat(3,minmax(0,0.7fr))_minmax(0,1.2fr)_auto] md:items-center">
        <div className="col-span-2 md:col-span-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/pecas/${r.part_id}`} className="font-mono text-sky-300 underline">{r.codigo_interno || "—"}</Link>
            {r.grupo.length > 1 && <span className="rounded bg-sky-950 px-1.5 text-[11px] text-sky-300">+{r.grupo.length - 1} consolidado(s): {r.grupo.slice(1).map((g) => g.codigo_interno).join(", ")}</span>}
            {r.possivel_duplicidade && <span className="rounded bg-amber-950 px-1.5 text-[11px] text-amber-300">possível duplicidade</span>}
            {!r.no_escopo && <span className="rounded bg-slate-800 px-1.5 text-[11px] text-slate-400">fora do escopo</span>}
          </div>
          <div className="truncate text-slate-100">{r.descricao}</div>
          <div className="text-xs text-slate-500">{r.locais_contados.length ? r.locais_contados.join(", ") : (r.localizacao || "Sem localização")}{r.responsaveis.length ? ` · ${r.responsaveis.join(", ")}` : ""}</div>
        </div>
        <div className="text-xs text-slate-400 md:text-right">Registrado<div className="font-mono text-base text-slate-200">{num(r.estoque_referencia)}</div></div>
        <div className="text-xs text-slate-400 md:text-right">Contado<div className="font-mono text-base text-slate-100">{r.quantidade_contada == null ? "—" : num(r.quantidade_contada)}</div></div>
        <div className="text-xs text-slate-400 md:text-right">Diferença<div className={`font-mono text-base font-semibold ${diffClass(r.diferenca)}`}>{signed(r.diferenca)}</div>
          {r.valor_divergencia ? <div className={`text-[11px] ${diffClass(r.diferenca)}`}>{brl(r.valor_divergencia)}</div> : null}</div>
        <div><span className={`rounded px-2 py-0.5 text-xs ${ROW_STYLE[r.status]}`}>{r.status_label}</span>
          {r.aprovador_nome && <div className="mt-1 text-[11px] text-slate-500">por {r.aprovador_nome} · {fmtDateTime(r.aprovado_em)}</div>}
          {r.motivo_recontagem && r.status === "RECONTAGEM" && <div className="mt-1 text-[11px] text-violet-300">{r.motivo_recontagem}</div>}</div>
        <div className="col-span-2 flex flex-wrap justify-end gap-2 md:col-span-1">
          {approvable && <Button size="sm" onClick={() => onAction("approve", r)} className="min-h-9 bg-emerald-700 text-white"><Check className="h-4 w-4 mr-1" />Aprovar</Button>}
          {editable && perms["inventario.recontagem"] && r.status !== "NAO_CONTADO" && r.status !== "RECONTAGEM" &&
            <Button size="sm" variant="outline" onClick={() => onAction("recount", r)} className="min-h-9 border-violet-500 bg-violet-950 text-violet-100"><RotateCcw className="h-4 w-4 mr-1" />Recontar</Button>}
          {r.contagens.length > 0 && <Button size="sm" variant="ghost" onClick={onToggle} aria-label="Ver contagens">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</Button>}
        </div>
      </div>
      {open && <div className="mt-3 space-y-2 rounded-lg bg-slate-950/70 p-3">
        {r.contagens.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="text-slate-300"><b className="font-mono text-slate-100">{num(c.quantidade_fisica)}</b> em {c.localizacao_nome || "sem localização"} · {c.usuario_nome} · {fmtDateTime(c.data)}
              {c.modo === "SOMAR" && <span className="text-sky-300"> · somada</span>}
              {c.codigo_lido && c.codigo_lido !== r.codigo_interno && <span className="text-amber-300"> · lida pelo código {c.codigo_lido}</span>}
              {c.versao > 1 && <span className="text-slate-500"> · corrigida</span>}
              {c.observacao && <div className="text-xs text-slate-500">{c.observacao}</div>}</div>
            {editable && perms["inventario.recontagem"] && <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onAction("correct", r, c)} className="border-sky-500 bg-sky-950 text-sky-100">Corrigir</Button>
              <Button size="sm" variant="outline" onClick={() => onAction("reject", r, c)} className="border-rose-500 bg-rose-950 text-rose-100">Rejeitar</Button>
            </div>}
          </div>
        ))}
      </div>}
    </div>
  );
}

function Conference({ inv, reloadInv }) {
  const [data, setData] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/inventories/${inv.id}/conference`, { params: { filtro: filtro || undefined, q: term || undefined, page } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [inv.id, filtro, term, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); setTerm(q); }, 350); return () => clearTimeout(t); }, [q]);

  const onAction = (kind, row, count) => {
    if (kind === "approve") setDialog({ kind, row, title: `Aprovar ${row.codigo_interno}?`, confirm: "Aprovar e ajustar estoque", required: false,
      text: `Saldo registrado ${row.estoque_referencia} → contado ${row.quantidade_contada} (${signed(row.diferenca)}).` + (row.grupo.length > 1 ? " Os cadastros consolidados vão para zero e o principal recebe a quantidade contada." : "") });
    if (kind === "recount") setDialog({ kind, row, title: `Pedir recontagem de ${row.codigo_interno}?`, confirm: "Pedir recontagem",
      text: "As contagens atuais ficam no histórico como canceladas e a peça volta para a lista de contagem." });
    if (kind === "correct") setDialog({ kind, row, count, qty: count.quantidade_fisica, title: "Corrigir contagem", confirm: "Salvar correção",
      text: "A contagem anterior fica no histórico. O estoque só muda na aprovação." });
    if (kind === "reject") setDialog({ kind, row, count, danger: true, title: "Rejeitar contagem?", confirm: "Rejeitar",
      text: `${count.quantidade_fisica} em ${count.localizacao_nome || "sem localização"} por ${count.usuario_nome}. Fica no histórico e não será aprovada.` });
  };
  const confirm = async (reason, qty) => {
    setBusy(true);
    const { kind, row, count } = dialog;
    try {
      if (kind === "approve") await api.post(`/inventories/${inv.id}/lines/${row.part_id}/approve`, { count_ids: row.count_ids, motivo: reason });
      if (kind === "recount") await api.post(`/inventories/${inv.id}/lines/${row.part_id}/recount`, { motivo: reason });
      if (kind === "correct") await api.post(`/inventories/counts/${count.id}/correct`, { quantidade_fisica: qty, motivo: reason });
      if (kind === "reject") await api.post(`/inventories/counts/${count.id}/cancel`, { motivo: reason });
      toast.success({ approve: "Aprovado. Estoque ajustado com movimentação registrada.", recount: "Recontagem solicitada.",
        correct: "Contagem corrigida.", reject: "Contagem rejeitada." }[kind]);
      setDialog(null); load(); reloadInv();
    } catch (e) { toast.error(apiErr(e)); load(); } finally { setBusy(false); }
  };
  const counts = data?.por_status || {};
  const total = (key) => key === "" ? Object.values(counts).reduce((a, b) => a + b, 0) : key === "duplicidade" ? null : (FILTER_STATUSES[key] || []).reduce((a, s) => a + (counts[s] || 0), 0);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => { setFiltro(key); setPage(1); }}
            className={`rounded-full border px-3 py-1 text-xs ${filtro === key ? "border-sky-500 bg-sky-900 text-white" : "border-slate-700 bg-slate-900 text-slate-300"}`}>
            {label}{total(key) != null ? ` (${num(total(key))})` : ""}
          </button>
        ))}
      </div>
      <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por código, descrição ou fabricante" className="pl-9 bg-slate-950 border-slate-700" /></div>
      {data === null ? <Skeleton className="h-64 bg-slate-800" /> : (
        <Card className="card-metal overflow-hidden p-0">
          {data.items.length === 0 && <div className="p-8 text-center text-slate-500">Nada nesta seleção.</div>}
          {data.items.map((r) => <ConferenceRow key={r.part_id} r={r} inv={inv} onAction={onAction} open={openRow === r.part_id} onToggle={() => setOpenRow(openRow === r.part_id ? null : r.part_id)} />)}
          {data.total_pages > 1 && <div className="flex items-center justify-center gap-3 border-t border-slate-800 p-3 text-sm text-slate-300">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-slate-700 bg-slate-900">Anterior</Button>
            Página {page} de {data.total_pages}
            <Button size="sm" variant="outline" disabled={page >= data.total_pages} onClick={() => setPage(page + 1)} className="border-slate-700 bg-slate-900">Próxima</Button>
          </div>}
        </Card>
      )}
      <ReasonDialog state={dialog} busy={busy} onClose={() => setDialog(null)} onConfirm={confirm} />
    </div>
  );
}

function Stat({ label, value, tone = "text-slate-100" }) {
  return <div className="rounded-xl border border-slate-800 bg-slate-900 p-3"><div className={`font-mono text-2xl font-bold ${tone}`}>{value}</div><div className="text-xs text-slate-400">{label}</div></div>;
}

function Dashboard({ inv }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/inventories/${inv.id}/dashboard`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e))); }, [inv.id, inv.write_revision, inv.status]);
  if (!d) return <Skeleton className="h-64 bg-slate-800" />;
  return (
    <div className="space-y-4">
      <Card className="card-metal p-4">
        <div className="mb-2 flex justify-between text-sm"><span className="text-slate-300">Progresso da contagem</span><span className="font-mono text-slate-100">{d.percentual}%</span></div>
        <div className="h-3 overflow-hidden rounded-full bg-slate-800" role="progressbar" aria-valuenow={d.percentual} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full bg-sky-500" style={{ width: `${Math.min(100, d.percentual)}%` }} />
        </div>
        <div className="mt-2 text-xs text-slate-400">{num(d.contados)} de {num(d.previstos)} peças contadas · {num(d.pendentes)} pendentes · {num(d.aprovados)} aprovadas</div>
      </Card>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Peças cadastradas (ativas)" value={num(d.total_pecas)} />
        <Stat label="Previstas para contagem" value={num(d.previstos)} />
        <Stat label="Já contadas" value={num(d.contados)} tone="text-sky-300" />
        <Stat label="Com divergência" value={num(d.divergentes)} tone="text-amber-300" />
        <Stat label="Possíveis duplicidades (pendentes)" value={num(d.auditoria?.pendentes || 0)} tone="text-amber-300" />
        <Stat label="Grupos consolidados" value={num(d.auditoria?.grupos_consolidados || 0)} tone="text-emerald-300" />
        <Stat label="Aguardando consolidação" value={num(d.auditoria?.aguardando_consolidacao || 0)} />
        <Stat label="Aguardando recontagem" value={num((d.por_status.RECONTAGEM || 0) + (d.por_status.ESTOQUE_MOVIMENTADO || 0) + (d.por_status.CONTAGEM_DUPLICADA || 0))} tone="text-violet-300" />
      </div>
      {d.valor_divergencias && <Card className="card-metal p-4 text-sm">
        <div className="mb-2 font-semibold text-slate-200">Valor estimado das divergências (custo médio cadastrado)</div>
        <div className="flex flex-wrap gap-6"><span className="text-rose-300">Faltas: {brl(d.valor_divergencias.faltas)}</span><span className="text-emerald-300">Sobras: {brl(d.valor_divergencias.sobras)}</span>
          {d.valor_divergencias.itens_sem_custo > 0 && <span className="text-slate-400">{d.valor_divergencias.itens_sem_custo} divergência(s) sem custo cadastrado</span>}</div>
      </Card>}
      <div className="grid gap-3 md:grid-cols-2">
        <Card className="card-metal p-4"><div className="mb-2 flex items-center gap-2 font-semibold text-slate-200"><Users className="h-4 w-4" />Equipe</div>
          {d.por_usuario.length === 0 && <p className="text-sm text-slate-500">Ninguém contou ainda.</p>}
          {d.por_usuario.map((u) => <div key={u.usuario} className="flex justify-between border-t border-slate-800 py-1.5 text-sm"><span className="text-slate-200">{u.usuario}</span><span className="text-slate-400">{num(u.contagens)} contagens · {num(u.pecas)} peças · {fmtDateTime(u.ultima)}</span></div>)}
        </Card>
        <Card className="card-metal p-4"><div className="mb-2 flex items-center gap-2 font-semibold text-slate-200"><MapPin className="h-4 w-4" />Por localização</div>
          {d.por_localizacao.map((l) => <div key={l.localizacao} className="flex justify-between border-t border-slate-800 py-1.5 text-sm"><span className="text-slate-200">{l.localizacao}</span><span className="text-slate-400">{num(l.contagens)} contagens</span></div>)}
        </Card>
      </div>
    </div>
  );
}

function HistoryTab({ inv }) {
  const [h, setH] = useState(null);
  useEffect(() => { api.get(`/inventories/${inv.id}/history`).then((r) => setH(r.data)).catch(() => setH({ historico: [], auditoria: [] })); }, [inv.id, inv.status]);
  if (!h) return <Skeleton className="h-48 bg-slate-800" />;
  return (
    <Card className="card-metal p-4 space-y-2 text-sm">
      {[...h.historico.map((e) => ({ ...e, fonte: "inv" })), ...h.auditoria.filter((a) => !String(a.acao).startsWith("INVENTARIO_") || a.acao === "INVENTARIO_LINHA_APROVADA")]
        .sort((a, b) => String(b.data).localeCompare(String(a.data))).map((e, i) => (
          <div key={i} className="border-t border-slate-800 pt-2 first:border-0 first:pt-0">
            <span className="text-slate-400">{fmtDateTime(e.data)}</span> · <b className="text-slate-200">{String(e.acao).replaceAll("_", " ").toLowerCase()}</b> · {e.usuario_nome}
            {e.detalhe?.motivo && <span className="text-slate-400"> · {e.detalhe.motivo}</span>}
            {e.detalhe?.ajustes && <span className="text-slate-400"> · {e.detalhe.ajustes.map((a) => `${a.codigo_interno}: ${a.antes}→${a.depois}`).join(", ")}</span>}
          </div>))}
    </Card>
  );
}

export default function InventoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [tab, setTab] = useState("conferencia");
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulk, setBulk] = useState(null);
  const load = useCallback(() => api.get(`/inventories/${id}/v2`).then((r) => setInv(r.data)).catch((e) => { toast.error(apiErr(e)); navigate("/inventario"); }), [id, navigate]);
  useEffect(() => { load(); }, [load]);
  if (!inv) return <Skeleton className="h-96 bg-slate-800" />;
  const runAction = async (reason) => {
    setBusy(true);
    try { await api.post(`/inventories/${id}/transition`, { acao: action, motivo: reason }); toast.success("Inventário atualizado."); setAction(null); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const approveAll = async () => {
    setBusy(true);
    try { const r = await api.post(`/inventories/${id}/approve-all`, {}); setBulk(r.data); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const countable = ["ABERTO", "EM_CONTAGEM", "AGUARDANDO_CONFERENCIA"].includes(inv.status) && inv.permissoes?.["inventario.contar"];
  return (
    <div className="space-y-5">
      <Link to="/inventario" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"><ArrowLeft className="h-4 w-4" />Inventários</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-50">{inv.descricao}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-400">
            <span className={`rounded px-2 py-0.5 text-xs ${INVENTORY_STATUS_STYLE[inv.status]}`}>{inv.status_label}</span>
            <span>{inv.tipo_label}</span>
            {(inv.filtros_nomes?.localizacao_ids || inv.filtros_nomes?.categoria_ids || []).length > 0 && <span>· {[...(inv.filtros_nomes.localizacao_ids || []), ...(inv.filtros_nomes.categoria_ids || [])].join(", ")}</span>}
            {inv.filtros?.part_ids?.length > 0 && <span>· {inv.filtros.part_ids.length} peças escolhidas</span>}
            <span>· responsável {inv.responsavel_nome}</span><span>· criado {fmtDateTime(inv.created_at)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {countable && <Button onClick={() => navigate(`/inventario/${id}/contar`)} className="bg-sky-700 hover:bg-sky-600"><ScanLine className="h-4 w-4 mr-1" />Contar peças</Button>}
          {inv.status === "AGUARDANDO_APROVACAO" && inv.permissoes?.["inventario.aprovar"] &&
            <Button disabled={busy} onClick={approveAll} className="bg-emerald-700 hover:bg-emerald-600"><Check className="h-4 w-4 mr-1" />Aprovar tudo que está pronto</Button>}
          {ACTIONS.filter(([key]) => inv.acoes?.[key]).map(([key, label, color]) => (
            <Button key={key} variant="secondary" onClick={() => setAction(key)} className={`${color} text-white`}>{label}</Button>
          ))}
        </div>
      </div>
      {inv.status === "AGUARDANDO_APROVACAO" && <p className="text-sm text-amber-300">Peças não contadas não terão o estoque alterado. Só o que foi contado e aprovado gera ajuste.</p>}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {[["conferencia", "Conferência"], ["painel", "Painel"], ["historico", "Histórico"]].map(([key, label]) => (
            <Button key={key} variant={tab === key ? "default" : "outline"} onClick={() => setTab(key)} className={tab === key ? "bg-sky-800 text-white" : "border-slate-700 bg-slate-900 text-slate-300"}>{label}</Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1 text-xs">
          {[["inventario", "Inventário"], ["ajustes", "Ajustes"], ["contagens", "Contagens"]].map(([tipo, label]) => (
            ["xlsx", "csv", "pdf"].map((f) => (
              <Button key={tipo + f} size="sm" variant="outline" className="border-slate-700 bg-slate-900 text-slate-300"
                onClick={() => downloadReport(`/inventories/${id}/report`, { tipo, formato: f }, `${tipo}-${inv.descricao}`)}>
                <Download className="h-3 w-3 mr-1" />{label} {f === "xlsx" ? "Excel" : f.toUpperCase()}
              </Button>))
          ))}
        </div>
      </div>
      {tab === "conferencia" && <Conference inv={inv} reloadInv={load} />}
      {tab === "painel" && <Dashboard inv={inv} />}
      {tab === "historico" && <HistoryTab inv={inv} />}
      <ReasonDialog state={action ? { title: ACTIONS.find(([k]) => k === action)?.[1] + "?", confirm: "Confirmar", required: action === "cancelar", danger: action === "cancelar",
        text: action === "cancelar" ? "Contagens e ajustes já aprovados continuam registrados. Nada mais poderá ser contado." : action === "finalizar" ? "Depois de finalizado, o inventário fica só para consulta." : "" } : null}
        busy={busy} onClose={() => setAction(null)} onConfirm={runAction} />
      <Dialog open={!!bulk} onOpenChange={(v) => { if (!v) setBulk(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Aprovação em lote</DialogTitle></DialogHeader>
          {bulk && <div className="space-y-2 text-sm">
            <p className="text-emerald-300">{bulk.aprovadas} peça(s) aprovadas e ajustadas.</p>
            {bulk.bloqueadas.length > 0 && <p className="text-violet-300">{bulk.bloqueadas.length} aguardando recontagem ou com contagem repetida: {bulk.bloqueadas.slice(0, 10).map((b) => b.codigo_interno).join(", ")}</p>}
            {bulk.falhas.map((f) => <p key={f.part_id} className="text-rose-300">{f.codigo_interno}: {f.motivo}</p>)}
            <p className="text-slate-400">{bulk.nao_contadas} peça(s) não contadas continuam com o estoque atual.</p>
          </div>}
          <DialogFooter><Button onClick={() => setBulk(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
